import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ReactTable, usePermanentRealtimeDealers } from "components";
import { useRegisterNavAction } from "hooks";
import "modules/HoldingsDashboard/components/HoldingsDashboardTable/HoldingsDashboardTable.scss";
import { useHoldingsQuery } from "modules/HoldingsDashboard/hooks";

import type { Holding } from "models";

type HoldingsDashboardTableProps = {
  onShowEditHoldingModal: (holding: Holding) => void;
};

const columnHelper = createColumnHelper<Holding>();

export const HoldingsDashboardTable = ({ onShowEditHoldingModal }: HoldingsDashboardTableProps) => {
  const { t } = useTranslation();

  const { dealers } = usePermanentRealtimeDealers();
  const { data: holdings, isFetching: isFetchingHoldings, refetch: refetchHoldings } = useHoldingsQuery();

  useRegisterNavAction(refetchHoldings);

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "name",
        size: 60,
        header: () => <span className="no-wrap-text"> {t("v8_name", "Name")} </span>,
        cell: ({ row: { original } }) => <span className="no-wrap-text">{original.name || ""}</span>
      }),
      columnHelper.accessor(row => row, {
        id: "dealers",
        header: () => <span className="no-wrap-text"> {t("v8_dealers", "Dealers")} </span>,
        cell: ({ row: { original } }) => {
          const dealerNames = original.dealer_ids.map(dealerId => dealers.find(dealer => dealer.id === dealerId)?.name).filter(Boolean);
          return <span className="dealer-names">{dealerNames.join(", ")}</span>;
        }
      })
    ],
    [t, dealers]
  );

  return (
    <div className="HoldingsDashboardTable">
      <ReactTable isFetching={isFetchingHoldings} columns={columns} data={holdings} onRowClick={onShowEditHoldingModal} />
    </div>
  );
};
